import {
    PulseStorage,
    StorageChangeKeyMatch,
    StorageChangeOrigin,
    deepObjectEqual,
} from '@pulse/common';
import { Observable, ReplaySubject } from 'rxjs';

export class StateService {
    private state?: Record<string, unknown>;
    private stateSubject$ = new ReplaySubject<Record<string, unknown>>(1);

    constructor(
        private storage: PulseStorage,
        private storageKey: string,
        public storageChangeOrigin: StorageChangeOrigin,
    ) {
        this.registerStorageChangeListeners();

        const initialState = this.getState();
        this.stateSubject$.next(initialState);
    }

    setState(state: Record<string, unknown>): void {
        const currentState = this.getState();
        const mergedState: Record<string, unknown> = {
            ...currentState,
            ...state,
        };

        if (deepObjectEqual(currentState, mergedState)) {
            return;
        }

        this.state = mergedState;
        this.storage.setJSON(this.storageKey, mergedState);
        this.stateSubject$.next(mergedState);
    }

    getState(): Record<string, unknown> {
        if (!this.state) {
            return this.refreshState();
        }

        return this.state;
    }

    getState$(): Observable<Record<string, unknown>> {
        return this.stateSubject$.asObservable();
    }

    removePropertyFromState(property: string): void {
        const currentState = this.getState();

        if (property in currentState) {
            const newState = { ...currentState };
            delete newState[property];

            if (!deepObjectEqual(currentState, newState)) {
                this.state = newState;
                this.storage.setJSON(this.storageKey, newState);
                this.stateSubject$.next(newState);
            }
        }
    }

    private refreshState(providedState?: Record<string, unknown>): Record<string, unknown> {
        const state = providedState || this.storage.getJSON(this.storageKey) || this.state || {};
        this.state = state;

        return state;
    }

    private registerStorageChangeListeners(): void {
        this.storage
            .change(this.storageKey, StorageChangeKeyMatch.Full, this.storageChangeOrigin)
            .subscribe(({ value }) => {
                const parsedState = JSON.parse(value || '{}');
                const previousState = this.getState();
                const updatedState = this.refreshState(parsedState);

                if (!deepObjectEqual(previousState, updatedState)) {
                    this.stateSubject$.next(updatedState);
                }
            });
    }
}
